<script setup>
import { ref, onMounted, onBeforeUnmount, nextTick } from 'vue';
import { useAlert } from 'dashboard/composables';
import MedupDealsAPI from 'dashboard/api/medupDeals';

const PER = 50;
const deals = ref([]);
const meta = ref({});
const page = ref(1);
const loading = ref(false); // первичная загрузка / пересортировка (прячет таблицу)
const loadingMore = ref(false); // дозагрузка следующих страниц (прокрутка)
const productsByDeal = ref({}); // bitrix_id -> [rows]
const expanded = ref(null);
const sortKey = ref('mis_date_created');
const sortDir = ref('desc');
const sentinel = ref(null);
const scrollRoot = ref(null);
let observer = null;

// header key -> sortable column on the server (must match get-deals whitelist)
const SORTABLE = {
  bitrix_id: 'bitrix_id',
  created_at: 'mis_date_created', // «Дата создания» = дата из МИС (фоллбэк на created_at, см. cell)
  deal_date: 'deal_date',
  patient_type_network: 'patient_type_network',
  doctor: 'doctor_lastname',
  department: 'bitrix_department_name',
  stage: 'bitrix_stage_name',
  deal_sum: 'deal_sum',
};
const sortBy = key => {
  const col = SORTABLE[key];
  if (!col) return;
  if (sortKey.value === col) {
    sortDir.value = sortDir.value === 'asc' ? 'desc' : 'asc';
  } else {
    sortKey.value = col;
    sortDir.value = 'desc';
  }
  reload();
};
const arrow = key =>
  sortKey.value === SORTABLE[key] ? (sortDir.value === 'asc' ? ' ▲' : ' ▼') : '';

// Подгружаем товары только для переданных сделок и доливаем в общую карту.
const loadProducts = async rows => {
  const ids = rows.map(d => String(d.bitrix_id)).filter(Boolean);
  if (!ids.length) return;
  try {
    const { data } = await MedupDealsAPI.products(ids);
    productsByDeal.value = { ...productsByDeal.value, ...(data.products || {}) };
  } catch (e) {
    // оставляем уже загруженное как есть
  }
};

// append=false — свежая выборка (первая страница / пересортировка), очищаем накопленное.
const fetchPage = async (p, { append = false } = {}) => {
  if (append) {
    loadingMore.value = true;
  } else {
    loading.value = true;
    expanded.value = null;
  }
  try {
    const { data } = await MedupDealsAPI.deals(p, PER, sortKey.value, sortDir.value);
    const rows = data.deals || [];
    if (append) {
      deals.value = deals.value.concat(rows);
    } else {
      deals.value = rows;
      productsByDeal.value = {};
    }
    meta.value = data.meta || {};
    page.value = p;
    loadProducts(rows);
  } catch (error) {
    useAlert('Не удалось загрузить сделки');
  } finally {
    loading.value = false;
    loadingMore.value = false;
  }
};

const reload = () => fetchPage(1);

const loadMore = () => {
  if (loading.value || loadingMore.value || !meta.value.has_more) return;
  fetchPage(page.value + 1, { append: true });
};

onMounted(async () => {
  await reload();
  await nextTick();
  observer = new IntersectionObserver(
    entries => {
      if (entries[0].isIntersecting) loadMore();
    },
    { root: scrollRoot.value, rootMargin: '600px' }
  );
  if (sentinel.value) observer.observe(sentinel.value);
});

onBeforeUnmount(() => {
  if (observer) observer.disconnect();
});

const productsOf = deal => productsByDeal.value[String(deal.bitrix_id)] || [];
const productCount = deal => productsOf(deal).length;
const productsSum = deal =>
  productsOf(deal).reduce((s, r) => s + (Number(r.row_sum) || 0), 0);
const toggleExpand = deal => {
  expanded.value = expanded.value === deal.bitrix_id ? null : deal.bitrix_id;
};

const fio = (last, name, second) =>
  [last, name, second].filter(Boolean).join(' ').trim();

const fmtDate = v => (v ? String(v).slice(0, 16) : '—');

const fmtSum = v => {
  const n = Number(v);
  return n ? `${n.toLocaleString('ru')} ₽` : '—';
};
</script>

<template>
  <div ref="scrollRoot" class="flex flex-col w-full h-full overflow-auto bg-n-surface-1">
    <div class="w-full px-6 py-6">
      <h1 class="mb-1 text-xl font-medium text-n-slate-12">Сделки</h1>
      <p class="mb-4 text-sm text-n-slate-11">
        Сделки (приёмы) из шины MedUp. Пациент, врач, оператор, филиал и стадия —
        из тех же справочников. Данные живые, постранично.
      </p>

      <div v-if="loading" class="py-10 text-center text-n-slate-11">Загрузка…</div>
      <div
        v-else-if="!deals.length"
        class="py-10 text-center text-n-slate-11"
      >
        Сделок нет.
      </div>

      <template v-else>
        <div class="overflow-x-auto border rounded-xl border-n-weak bg-n-solid-1">
          <table class="w-full text-sm border-collapse">
            <thead>
              <tr class="border-b border-n-weak">
                <th class="px-3 py-2 font-medium text-left cursor-pointer select-none text-n-slate-11 whitespace-nowrap hover:text-n-slate-12" @click="sortBy('bitrix_id')">Bitrix ID{{ arrow('bitrix_id') }}</th>
                <th class="px-3 py-2 font-medium text-left cursor-pointer select-none text-n-slate-11 whitespace-nowrap hover:text-n-slate-12" @click="sortBy('created_at')">Дата создания{{ arrow('created_at') }}</th>
                <th class="px-3 py-2 font-medium text-left cursor-pointer select-none text-n-slate-11 whitespace-nowrap hover:text-n-slate-12" @click="sortBy('deal_date')">Дата приёма{{ arrow('deal_date') }}</th>
                <th class="px-3 py-2 font-medium text-left text-n-slate-11 whitespace-nowrap">Пациент</th>
                <th class="px-3 py-2 font-medium text-left cursor-pointer select-none text-n-slate-11 whitespace-nowrap hover:text-n-slate-12" @click="sortBy('patient_type_network')">Тип пациента{{ arrow('patient_type_network') }}</th>
                <th class="px-3 py-2 font-medium text-left cursor-pointer select-none text-n-slate-11 whitespace-nowrap hover:text-n-slate-12" @click="sortBy('doctor')">Врач{{ arrow('doctor') }}</th>
                <th class="px-3 py-2 font-medium text-left text-n-slate-11 whitespace-nowrap">Специализация</th>
                <th class="px-3 py-2 font-medium text-left text-n-slate-11 whitespace-nowrap">Оператор</th>
                <th class="px-3 py-2 font-medium text-left cursor-pointer select-none text-n-slate-11 whitespace-nowrap hover:text-n-slate-12" @click="sortBy('department')">Филиал{{ arrow('department') }}</th>
                <th class="px-3 py-2 font-medium text-left cursor-pointer select-none text-n-slate-11 whitespace-nowrap hover:text-n-slate-12" @click="sortBy('stage')">Стадия{{ arrow('stage') }}</th>
                <th class="px-3 py-2 font-medium text-left text-n-slate-11 whitespace-nowrap">ID стадии</th>
                <th class="px-3 py-2 font-medium text-left text-n-slate-11 whitespace-nowrap">Повод назначения</th>
                <th class="px-3 py-2 font-medium text-left text-n-slate-11 whitespace-nowrap">Тип</th>
                <th class="px-3 py-2 font-medium text-right cursor-pointer select-none text-n-slate-11 whitespace-nowrap hover:text-n-slate-12" @click="sortBy('deal_sum')">Сумма{{ arrow('deal_sum') }}</th>
                <th class="px-3 py-2 font-medium text-left text-n-slate-11 whitespace-nowrap">Товары</th>
              </tr>
            </thead>
            <tbody>
              <template v-for="d in deals" :key="d.id">
              <tr
                class="border-b border-n-weak hover:bg-n-alpha-1 cursor-pointer"
                @click="toggleExpand(d)"
              >
                <td class="px-3 py-2 font-mono text-xs text-n-slate-11 whitespace-nowrap">
                  {{ d.bitrix_id || '—' }}
                </td>
                <td class="px-3 py-2 text-n-slate-11 whitespace-nowrap tabular-nums">
                  {{ fmtDate(d.mis_date_created || d.created_at) }}
                </td>
                <td class="px-3 py-2 text-n-slate-11 whitespace-nowrap tabular-nums">
                  {{ fmtDate(d.deal_date || d.time_deal_start) }}
                </td>
                <td class="px-3 py-2 font-medium text-n-slate-12 whitespace-nowrap">
                  <span v-if="d.patient_name">{{ d.patient_name }}</span>
                  <span v-else class="text-n-slate-10">#{{ d.bitrix_patients_id }}</span>
                </td>
                <td class="px-3 py-2 text-n-slate-11 whitespace-nowrap">
                  {{ d.patient_type_network || '—' }}
                </td>
                <td class="px-3 py-2 text-n-slate-11 whitespace-nowrap">
                  {{ fio(d.doctor_lastname, d.doctor_name, d.doctor_secondname) || '—' }}
                </td>
                <td class="px-3 py-2 text-n-slate-11 whitespace-nowrap">
                  {{ d.bitrix_specialization_name || d.mis_specialization_name || '—' }}
                </td>
                <td class="px-3 py-2 text-n-slate-11 whitespace-nowrap">
                  {{ fio(d.assigned_by_lastname, d.assigned_by_name) || '—' }}
                </td>
                <td class="px-3 py-2 text-n-slate-11 whitespace-nowrap">
                  {{ d.bitrix_department_name || '—' }}
                </td>
                <td class="px-3 py-2 text-n-slate-11 whitespace-nowrap">
                  {{ d.bitrix_stage_name || d.bitrix_stage_id || '—' }}
                </td>
                <td class="px-3 py-2 font-mono text-xs text-n-slate-11 whitespace-nowrap">
                  {{ d.bitrix_stage_id || '—' }}
                </td>
                <td class="px-3 py-2 text-n-slate-11 whitespace-nowrap">
                  {{ d.appoint_reason || '—' }}
                </td>
                <td class="px-3 py-2 text-n-slate-11 whitespace-nowrap">
                  {{ d.deals_destination_type_name || d.type_id || '—' }}
                </td>
                <td class="px-3 py-2 text-right text-n-slate-11 whitespace-nowrap tabular-nums">
                  {{ fmtSum(d.deal_sum) }}
                </td>
                <td class="px-3 py-2 whitespace-nowrap">
                  <span v-if="productCount(d)" class="text-n-brand">
                    {{ expanded === d.bitrix_id ? '▾' : '▸' }} {{ productCount(d) }} поз.
                  </span>
                  <span v-else class="text-n-slate-10">—</span>
                </td>
              </tr>
              <tr v-if="expanded === d.bitrix_id" class="border-b border-n-weak bg-n-alpha-1">
                <td colspan="15" class="px-6 py-3">
                  <div class="mb-1 text-xs font-medium tracking-wide uppercase text-n-slate-10">
                    Товары / услуги сделки #{{ d.bitrix_id }}
                  </div>
                  <table class="w-full max-w-4xl text-sm">
                    <thead>
                      <tr class="text-xs uppercase border-b text-n-slate-10 border-n-weak">
                        <th class="py-1.5 pr-6 font-medium text-left">Услуга / товар</th>
                        <th class="py-1.5 pr-6 font-medium text-left">Прайс-группа</th>
                        <th class="py-1.5 pr-6 font-medium text-right">Кол-во</th>
                        <th class="py-1.5 pr-6 font-medium text-right">Цена за ед.</th>
                        <th class="py-1.5 pr-6 font-medium text-right">Скидка</th>
                        <th class="py-1.5 font-medium text-right">Сумма</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="(p, i) in productsOf(d)"
                        :key="i"
                        class="border-b text-n-slate-11 border-n-weak/50"
                      >
                        <td class="py-1.5 pr-6 text-n-slate-12">{{ p.products_name || '—' }}</td>
                        <td class="py-1.5 pr-6 text-n-slate-10">{{ p.price_groups_name || '—' }}</td>
                        <td class="py-1.5 pr-6 text-right tabular-nums">{{ p.quantity }}</td>
                        <td class="py-1.5 pr-6 text-right tabular-nums">{{ fmtSum(p.price) }}</td>
                        <td class="py-1.5 pr-6 text-right tabular-nums text-n-ruby-11">
                          {{ Number(p.discount_sum) ? '−' + fmtSum(p.discount_sum) : '—' }}
                        </td>
                        <td class="py-1.5 font-medium text-right tabular-nums text-n-slate-12">
                          {{ fmtSum(p.row_sum) }}
                        </td>
                      </tr>
                      <tr class="border-t border-n-weak">
                        <td class="py-2 pr-6 font-medium text-n-slate-12" colspan="5">
                          Итого по позициям · {{ productCount(d) }} шт.
                        </td>
                        <td class="py-2 font-semibold text-right tabular-nums text-n-slate-12">
                          {{ fmtSum(productsSum(d)) }}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </td>
              </tr>
              </template>
            </tbody>
          </table>
        </div>

      </template>

      <!-- триггер дозагрузки: попадает в зону видимости при прокрутке к низу -->
      <div ref="sentinel" class="h-px"></div>
      <div v-if="loadingMore" class="py-4 text-center text-sm text-n-slate-11">
        Загрузка…
      </div>
      <div
        v-else-if="deals.length && !meta.has_more"
        class="py-4 text-center text-xs text-n-slate-10"
      >
        Все сделки загружены · {{ deals.length }}
      </div>
    </div>
  </div>
</template>
